import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Spin, Alert, message, Card, Button, Typography, Row, Col } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';

// Import API service
import * as lessonPlanApi from './services/lessonPlanApi';

// Import child components
import LessonPlanManagerTable from './LessonPlanManagerTable';
import LessonPlanWizard from './LessonPlanWizard';
import InteractiveLessonPlan from './InteractiveLessonPlan';

const { Content } = Layout;
const { Title } = Typography;

const LessonPlanManager = () => {
    // --- View State ---
    const [view, setView] = useState('LIBRARY'); // 'LIBRARY', 'WIZARD', 'PREVIEW'
    const [editingLesson, setEditingLesson] = useState(null); // Holds the full lesson for the preview

    // --- Shared Data State ---
    const [lessonPlans, setLessonPlans] = useState([]);
    const [wizardConfig, setWizardConfig] = useState(null);
    const [curriculumData, setCurriculumData] = useState(null);
    const [instructionalBlocks, setInstructionalBlocks] = useState([]);

    // --- Loading & Error State ---
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLibraryLoading, setIsLibraryLoading] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // --- Data Fetching Callbacks ---

    // Fetches the library list (memoized to be reusable)
    const fetchLibrary = useCallback(async () => {
        setIsLibraryLoading(true);
        try {
            const library = await lessonPlanApi.searchLessonPlans();
            setLessonPlans(library?.content || []);
            setSelectedRowKeys([]); // Clear selection on refresh
        } catch (err) {
            message.error(err.message || 'Failed to refresh lesson plans.');
        } finally {
            setIsLibraryLoading(false);
        }
    }, []); // No dependencies, this function is stable

    // Fetches all required data on initial component mount
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setIsInitialLoading(true);
                // Fetch all non-library data in parallel
                const [config, curriculum, blocks] = await Promise.all([
                    lessonPlanApi.getWizardConfig(),
                    lessonPlanApi.getCurriculumData(),
                    lessonPlanApi.getInstructionalBlocks(),
                ]);

                setWizardConfig(config);
                setCurriculumData(curriculum);
                setInstructionalBlocks(blocks);

                // Fetch the library last
                await fetchLibrary();
                setError(null);
            } catch (err) {
                const errorMessage = err.message || 'Failed to load initial data.';
                setError(errorMessage);
                message.error(errorMessage);
            } finally {
                setIsInitialLoading(false);
            }
        };
        fetchInitialData();
    }, [fetchLibrary]); // Run once on mount

    // --- Handler Functions ---

    // Passed to LearningPlans: Switches view to the wizard
    const handleCreateNew = () => {
        setView('WIZARD');
    };

    // Passed to LearningPlans: Fetches full lesson, switches view to preview
    const handleEdit = async (lessonRecord) => {
        if (!lessonRecord || !lessonRecord.key) {
            message.error('Invalid lesson record.');
            return;
        }
        const lessonId = lessonRecord.key;
        setIsEditLoading(true);

        try {
            const fullLessonPlan = await lessonPlanApi.getLessonPlanDetails(lessonId);

            if (!fullLessonPlan.formValues) {
                console.warn('Lesson missing formValues, some defaults may be incorrect.');
            }
            if (!fullLessonPlan.lessonPlanContent) {
                throw new Error('Lesson plan content is missing.');
            }

            if (fullLessonPlan.formValues?.lessonPlanTemplateId) {
                try {
                    const templateBlocks = await lessonPlanApi.getInstructionalBlocks(
                        fullLessonPlan.formValues.lessonPlanTemplateId
                    );
                    setInstructionalBlocks(templateBlocks);
                } catch (blocksErr) {
                    console.warn(`Failed to fetch template-specific blocks: ${blocksErr.message}. Falling back to generic blocks.`);
                    setInstructionalBlocks([]);
                }
            } else {
                setInstructionalBlocks([]);
            }

            setEditingLesson({
                ...fullLessonPlan,
                key: lessonId
            });
            setView('PREVIEW');
        } catch (err) {
            message.error({ content: `Failed to load lesson: ${err.message || 'Unknown error'}`, key: 'edit-load' });
        } finally {
            setIsEditLoading(false);
        }
    };

    // Passed to Wizard & Preview: Returns to library and refreshes
    const handleReturnToLibrary = (refresh = true) => {
        setInstructionalBlocks([]);
        setView('LIBRARY');
        setEditingLesson(null);
        if (refresh) {
            fetchLibrary(); // Re-fetch the library
        }
    };

    // Passed to LearningPlans: Handles archiving selected plans
    const handleArchive = async () => {
        if (selectedRowKeys.length === 0) return;
        setIsLibraryLoading(true);
        try {
            await lessonPlanApi.archiveLessonPlans({ keys: selectedRowKeys });
            message.success(`${selectedRowKeys.length} lesson plan(s) archived.`);
            // Manually filter state for immediate UI update
            setLessonPlans(prev => prev.filter(p => !selectedRowKeys.includes(p.key)));
            setSelectedRowKeys([]); // Clear selection
        } catch (err) {
            message.error(err.message || 'Failed to archive plans.');
        } finally {
            setIsLibraryLoading(false);
        }
    };

    // --- Render Logic ---

    const renderHeader = () => {
        // No header in the wizard, it has its own
        if (view === 'WIZARD') return null;

        return (
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                    <Title level={2} style={{ margin: 0 }}>
                        {view === 'LIBRARY' && 'Lesson Plans'}
                        {view === 'PREVIEW' && 'Edit Lesson Plan'}
                    </Title>
                </Col>
                <Col>
                    {view === 'LIBRARY' && (
                        <Button
                            type="primary"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={handleCreateNew}
                        >
                            Create New Plan
                        </Button>
                    )}
                    {view === 'PREVIEW' && (
                        <Button
                            size="large"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => handleReturnToLibrary(false)} // Don't refresh, save does that
                        >
                            Back to Library
                        </Button>
                    )}
                </Col>
            </Row>
        );
    };

    const renderContent = () => {
        if (isInitialLoading) {
            return <Spin description="Loading Lesson Plan Manager..." size="large" style={{ display: 'block', margin: '50px auto' }} />;
        }
        if (error) {
            return <Alert message="Error" description={error} type="error" showIcon style={{ maxWidth: '800px', margin: '20px auto' }} />;
        }

        switch (view) {
            case 'WIZARD':
                return (
                    <LessonPlanWizard
                        // Pass all shared data as props
                        wizardConfig={wizardConfig}
                        curriculumData={curriculumData}
                        instructionalBlocks={instructionalBlocks}
                        // Pass the "close" handler
                        onClose={() => handleReturnToLibrary(true)}
                    />
                );
            case 'PREVIEW':
                return (
                    <Card style={{ padding: '16px' }}>
                        <InteractiveLessonPlan
                            formValues={editingLesson.formValues || {}} // Pass formValues
                            lessonContent={editingLesson.lessonPlanContent}
                            instructionalBlocks={instructionalBlocks}
                            contentTypes={wizardConfig?.contentTypes || {}}
                            editingLessonId={editingLesson.key} // Pass the ID for saving
                        />
                    </Card>
                );
            case 'LIBRARY':
            default:
                return (
                    <Card style={{ padding: '16px' }}>
                        <LessonPlanManagerTable
                            lessonPlans={lessonPlans}
                            isLoading={isLibraryLoading}
                            isEditLoading={isEditLoading}
                            selectedRowKeys={selectedRowKeys}
                            rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                            onEdit={handleEdit}
                            onArchive={handleArchive}
                        />
                    </Card>
                );
        }
    };

    return (
        <Layout style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            <Content>
                {renderHeader()}
                {renderContent()}
            </Content>
        </Layout>
    );
};

export default LessonPlanManager;